import React from 'react';
import { OrderSummary } from '../../../../types/sales.types';
import { useFinancialSummaryCard } from './FinancialSummaryCard.hooks';
import {
  StyledCard,
  StyledCardContent,
  CardTitle,
  ContentContainer,
  FinancialRow,
  RowLabel,
  RowValue,
  ErrorRowLabel,
  ErrorRowValue,
  StyledDivider,
  TotalRow,
  TotalLabel,
  TotalValue,
  PaymentDetailsContainer,
  SuccessRowLabel,
  SuccessRowValue,
  WarningRowLabel,
  WarningRowValue,
} from './FinancialSummaryCard.styles';

interface FinancialSummaryCardProps {
  orderSummary: OrderSummary;
}

const FinancialSummaryCard: React.FC<FinancialSummaryCardProps> = ({ orderSummary }) => {
  const {
    formattedSubtotal,
    formattedDiscount,
    formattedMarkup,
    formattedTotalCharges,
    formattedTotal,
    formattedAmountPaid,
    formattedChange,
    formattedPendingBalance,
    hasDiscount,
    hasMarkup,
    hasCharges,
    hasChange,
    hasPendingBalance,
  } = useFinancialSummaryCard({ orderSummary });

  return (
    <StyledCard>
      <StyledCardContent>
        <CardTitle variant="h6">Resumen Financiero</CardTitle>

        <ContentContainer>
          {/* Subtotal */}
          <FinancialRow>
            <RowLabel variant="body2">Subtotal:</RowLabel>
            <RowValue variant="body2">{formattedSubtotal}</RowValue>
          </FinancialRow>

          {/* Descuentos (solo si hay) */}
          {hasMarkup && (
            <FinancialRow>
              <RowLabel variant="body2">Incrementos:</RowLabel>
              <RowValue variant="body2" sx={{ color: 'success.main' }}>
                +{formattedMarkup}
              </RowValue>
            </FinancialRow>
          )}

          {hasDiscount && (
            <FinancialRow>
              <ErrorRowLabel variant="body2">Descuentos:</ErrorRowLabel>
              <ErrorRowValue variant="body2">-{formattedDiscount}</ErrorRowValue>
            </FinancialRow>
          )}

          {hasCharges && (
            <FinancialRow>
              <RowLabel variant="body2">Cargos adicionales:</RowLabel>
              <RowValue variant="body2">{formattedTotalCharges}</RowValue>
            </FinancialRow>
          )}

          <StyledDivider />

          {/* Total */}
          <TotalRow>
            <TotalLabel variant="h6">Total:</TotalLabel>
            <TotalValue variant="h6">{formattedTotal}</TotalValue>
          </TotalRow>

          <StyledDivider />

          {/* Detalles de pago */}
          <PaymentDetailsContainer>
            <FinancialRow>
              <RowLabel variant="body2">Monto recibido:</RowLabel>
              <RowValue variant="body2">{formattedAmountPaid}</RowValue>
            </FinancialRow>

            {/* Cambio (solo si hay) */}
            {hasChange && (
              <FinancialRow>
                <SuccessRowLabel variant="body2">Cambio:</SuccessRowLabel>
                <SuccessRowValue variant="body2">{formattedChange}</SuccessRowValue>
              </FinancialRow>
            )}

            {/* Saldo pendiente (solo si hay) */}
            {hasPendingBalance && (
              <FinancialRow>
                <WarningRowLabel variant="body2">Saldo pendiente:</WarningRowLabel>
                <WarningRowValue variant="body2">{formattedPendingBalance}</WarningRowValue>
              </FinancialRow>
            )}
          </PaymentDetailsContainer>
        </ContentContainer>
      </StyledCardContent>
    </StyledCard>
  );
};

export default FinancialSummaryCard;
